(function () {
  const MIN_SCALE = 1;
  const MAX_SCALE = 2;
  const DOUBLE_TAP_DELAY_MS = 280;
  const DOUBLE_TAP_DISTANCE_PX = 24;
  const INTERSECTION_THRESHOLDS = [0.25, 0.5, 0.75];

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function clampScale(scale) {
    return clamp(scale, MIN_SCALE, MAX_SCALE);
  }

  function clampPan(transform, viewport) {
    if (!viewport.width || !viewport.height || transform.scale <= MIN_SCALE) {
      return { x: 0, y: 0 };
    }

    const maxX = Math.max(0, (viewport.width * (transform.scale - 1)) / 2);
    const maxY = Math.max(0, (viewport.height * (transform.scale - 1)) / 2);

    return {
      x: clamp(transform.x, -maxX, maxX),
      y: clamp(transform.y, -maxY, maxY),
    };
  }

  function getNextTransformForScaleAtPoint({ prev, nextScale, viewport, clientPoint }) {
    const clampedScale = clampScale(nextScale);

    if (clampedScale <= MIN_SCALE) {
      return { x: 0, y: 0, scale: MIN_SCALE };
    }

    const centerX = viewport.width / 2;
    const centerY = viewport.height / 2;
    const pointX = clientPoint.x - centerX;
    const pointY = clientPoint.y - centerY;

    const localX = (pointX - prev.x) / prev.scale;
    const localY = (pointY - prev.y) / prev.scale;

    return {
      x: prev.x + localX * (prev.scale - clampedScale),
      y: prev.y + localY * (prev.scale - clampedScale),
      scale: clampedScale,
    };
  }

  function pointerDistance(pointA, pointB) {
    return Math.hypot(pointA.x - pointB.x, pointA.y - pointB.y);
  }

  function pointerMidpoint(pointA, pointB) {
    return {
      x: (pointA.x + pointB.x) / 2,
      y: (pointA.y + pointB.y) / 2,
    };
  }

  class ProductMediaModalZoomController {
    constructor(modal) {
      this.modal = modal;
      this.content = null;
      this.mediaItems = [];
      this.mediaById = new Map();
      this.dotButtons = [];
      this.stateByMediaId = new Map();
      this.activeMediaId = null;

      this.bound = false;
      this.intersectionObserver = null;
      this.mutationObserver = null;

      this.pointerDrag = null;
      this.touchPoints = new Map();
      this.pinchSession = null;
      this.lastTap = null;

      this.onDotClick = this.onDotClick.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onDoubleClick = this.onDoubleClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
      this.onResize = this.onResize.bind(this);

      this.refresh();
    }

    onShow(openerMediaId) {
      this.refresh();
      this.bind();
      this.resetAll();

      if (openerMediaId) {
        this.activateMedia(String(openerMediaId), { scrollIntoView: false });
      } else {
        this.updateActiveMediaFromViewport();
      }
    }

    onHide() {
      this.resetAll();
      this.stopPointerInteractions();
      this.unbind();
    }

    destroy() {
      this.unbind();
      this.stopPointerInteractions();
      this.intersectionObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.intersectionObserver = null;
      this.mutationObserver = null;
    }

    refresh() {
      this.content = this.modal.querySelector('.product-media-modal__content');
      if (!this.content) return;

      this.mediaItems = Array.from(this.content.querySelectorAll('[data-media-id]')).filter(
        (element) => element.closest('.product-media-modal__content') === this.content
      );

      this.mediaById.clear();
      this.mediaItems.forEach((element) => {
        this.mediaById.set(String(element.dataset.mediaId), element);
      });

      this.dotButtons = Array.from(this.modal.querySelectorAll('[data-media-modal-dot]'));
      this.dotButtons.forEach((button) => {
        if (!button.dataset.mediaId) {
          const parentMedia = button.closest('[data-media-id]');
          if (parentMedia?.dataset.mediaId) {
            button.dataset.mediaId = String(parentMedia.dataset.mediaId);
          }
        }
      });

      const nextStateByMediaId = new Map();
      this.mediaItems.forEach((mediaElement) => {
        if (!mediaElement.classList.contains('product-media-modal__image-frame')) return;

        const mediaId = String(mediaElement.dataset.mediaId);
        const image = this.getImageForMedia(mediaElement);
        if (!image) return;

        image.classList.add('product-media-modal__zoom-target');
        const prevState = this.stateByMediaId.get(mediaId);
        nextStateByMediaId.set(mediaId, prevState ? { ...prevState } : { x: 0, y: 0, scale: MIN_SCALE });
      });

      this.stateByMediaId = nextStateByMediaId;
      this.rebuildIntersectionObserver();

      if (!this.mutationObserver) {
        this.mutationObserver = new MutationObserver(() => {
          this.refresh();
          this.updateActiveMediaFromViewport();
        });
      }

      this.mutationObserver.disconnect();
      this.mutationObserver.observe(this.content, { childList: true, subtree: true });
    }

    bind() {
      if (this.bound || !this.content) return;

      this.modal.addEventListener('click', this.onDotClick);
      this.content.addEventListener('wheel', this.onWheel, { passive: false });
      this.content.addEventListener('dblclick', this.onDoubleClick);
      this.content.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerCancel);
      window.addEventListener('resize', this.onResize);

      this.bound = true;
    }

    unbind() {
      if (!this.bound || !this.content) return;

      this.modal.removeEventListener('click', this.onDotClick);
      this.content.removeEventListener('wheel', this.onWheel);
      this.content.removeEventListener('dblclick', this.onDoubleClick);
      this.content.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerCancel);
      window.removeEventListener('resize', this.onResize);

      this.bound = false;
    }

    onResize() {
      this.stateByMediaId.forEach((_state, mediaId) => {
        const mediaElement = this.mediaById.get(mediaId);
        if (!mediaElement) return;
        const viewport = this.getViewportForMedia(mediaElement);
        const current = this.getTransform(mediaId);
        const clamped = clampPan(current, viewport);
        this.setTransform(mediaId, { ...current, ...clamped });
      });
      this.updateActiveMediaFromViewport();
    }

    onDotClick(event) {
      const button = event.target.closest('[data-media-modal-dot]');
      if (!button || !this.modal.contains(button)) return;

      const targetMediaId = String(button.dataset.mediaId || '');
      if (!targetMediaId || !this.mediaById.has(targetMediaId)) return;

      event.preventDefault();

      if (this.activeMediaId) {
        this.resetMedia(this.activeMediaId);
      }

      this.activateMedia(targetMediaId, { scrollIntoView: true });
    }

    onWheel(event) {
      const mediaElement = event.target.closest('[data-media-id].product-media-modal__image-frame');
      if (!mediaElement || !this.content?.contains(mediaElement)) return;
      if (!mediaElement?.dataset.mediaId) return;

      event.preventDefault();
      this.activateMedia(String(mediaElement.dataset.mediaId), { scrollIntoView: false });

      const mediaId = String(mediaElement.dataset.mediaId);
      const current = this.getTransform(mediaId);
      const deltaY =
        event.deltaY *
        (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? (typeof window !== 'undefined' ? window.innerHeight : 1) : 1);
      const scaleFactor = Math.exp(-deltaY * 0.0015);
      const nextScale = clampScale(current.scale * scaleFactor);

      this.setScaleAtPoint(mediaId, nextScale, { x: event.clientX, y: event.clientY });
    }

    onDoubleClick(event) {
      const mediaElement = event.target.closest('[data-media-id].product-media-modal__image-frame');
      if (!mediaElement || !this.content?.contains(mediaElement)) return;
      if (!mediaElement?.dataset.mediaId) return;

      event.preventDefault();
      const mediaId = String(mediaElement.dataset.mediaId);

      this.activateMedia(mediaId, { scrollIntoView: false });
      this.toggleScale(mediaId, { x: event.clientX, y: event.clientY });
    }

    onPointerDown(event) {
      const mediaElement = event.target.closest('[data-media-id].product-media-modal__image-frame');
      if (!mediaElement || !this.content?.contains(mediaElement)) return;
      if (!mediaElement?.dataset.mediaId) return;

      const image = this.getImageForMedia(mediaElement);
      if (!image) return;

      const mediaId = String(mediaElement.dataset.mediaId);
      this.activateMedia(mediaId, { scrollIntoView: false });

      if (event.pointerType === 'touch') {
        this.touchPoints.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
          startX: event.clientX,
          startY: event.clientY,
          mediaId,
        });

        const touchPoints = this.getTouchPointsForMedia(mediaId);
        if (touchPoints.length === 2) {
          this.startPinch(mediaId, touchPoints[0], touchPoints[1]);
          return;
        }
      }

      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const current = this.getTransform(mediaId);
      if (current.scale <= MIN_SCALE) return;

      this.pointerDrag = {
        mediaId,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: current.x,
        originY: current.y,
        moved: false,
      };

      if (image.setPointerCapture) {
        image.setPointerCapture(event.pointerId);
      }

      mediaElement.classList.add('is-panning');
      this.content.classList.add('is-panning');
      event.preventDefault();
    }

    onPointerMove(event) {
      if (event.pointerType === 'touch' && this.touchPoints.has(event.pointerId)) {
        const existing = this.touchPoints.get(event.pointerId);
        this.touchPoints.set(event.pointerId, {
          ...existing,
          x: event.clientX,
          y: event.clientY,
        });
      }

      if (this.pinchSession) {
        const touchPoints = this.getTouchPointsForMedia(this.pinchSession.mediaId);
        if (touchPoints.length < 2) return;

        const [pointA, pointB] = touchPoints;
        const distance = pointerDistance(pointA, pointB);
        if (!this.pinchSession.startDistance) return;

        const nextScale = clampScale((this.pinchSession.startTransform.scale * distance) / this.pinchSession.startDistance);
        const midpoint = pointerMidpoint(pointA, pointB);
        this.setScaleAtPoint(this.pinchSession.mediaId, nextScale, midpoint, this.pinchSession.startTransform);

        event.preventDefault();
        return;
      }

      if (!this.pointerDrag || this.pointerDrag.pointerId !== event.pointerId) return;

      const mediaElement = this.mediaById.get(this.pointerDrag.mediaId);
      if (!mediaElement) return;

      const viewport = this.getViewportForMedia(mediaElement);
      const current = this.getTransform(this.pointerDrag.mediaId);
      const dx = event.clientX - this.pointerDrag.startX;
      const dy = event.clientY - this.pointerDrag.startY;
      const moved = Math.abs(dx) > 2 || Math.abs(dy) > 2;
      this.pointerDrag.moved = this.pointerDrag.moved || moved;

      const clamped = clampPan(
        {
          scale: current.scale,
          x: this.pointerDrag.originX + dx,
          y: this.pointerDrag.originY + dy,
        },
        viewport
      );

      this.setTransform(this.pointerDrag.mediaId, { scale: current.scale, ...clamped });
      event.preventDefault();
    }

    onPointerUp(event) {
      this.handlePointerFinish(event);
    }

    onPointerCancel(event) {
      this.handlePointerFinish(event);
    }

    handlePointerFinish(event) {
      const touchPoint = this.touchPoints.get(event.pointerId);
      const touchMoved =
        !!touchPoint &&
        pointerDistance(
          { x: touchPoint.startX, y: touchPoint.startY },
          { x: touchPoint.x, y: touchPoint.y }
        ) > 8;
      const mediaFromTarget =
        event.target instanceof Element ? event.target.closest('[data-media-id]') : null;

      if (touchPoint) {
        this.touchPoints.delete(event.pointerId);
      }

      const wasPinching = !!this.pinchSession;
      let canEvaluateDoubleTap = false;
      let mediaIdForTap = touchPoint?.mediaId || null;
      let moved = false;

      if (this.pointerDrag && this.pointerDrag.pointerId === event.pointerId) {
        moved = this.pointerDrag.moved;
        mediaIdForTap = this.pointerDrag.mediaId;
        canEvaluateDoubleTap = event.pointerType === 'touch' && !moved && !this.pinchSession;
        this.stopDrag();
      }

      if (this.pinchSession && this.getTouchPointsForMedia(this.pinchSession.mediaId).length < 2) {
        this.pinchSession = null;
      }

      if (
        !mediaIdForTap &&
        mediaFromTarget?.dataset?.mediaId &&
        this.content?.contains(mediaFromTarget) &&
        mediaFromTarget.classList.contains('product-media-modal__image-frame')
      ) {
        mediaIdForTap = String(mediaFromTarget.dataset.mediaId);
      }

      if (!canEvaluateDoubleTap && event.pointerType === 'touch' && mediaIdForTap && !touchMoved && !wasPinching) {
        canEvaluateDoubleTap = true;
      }

      if (canEvaluateDoubleTap && mediaIdForTap) {
        this.handleTouchTap(event, mediaIdForTap);
      }

      if (event.pointerType === 'mouse' && mediaIdForTap && !moved && !wasPinching) {
        this.toggleScale(mediaIdForTap, { x: event.clientX, y: event.clientY });
      }

      if (!this.pointerDrag && !this.pinchSession) {
        this.content?.classList.remove('is-panning');
      }
    }

    handleTouchTap(event, mediaId) {
      const now = Date.now();
      const currentTap = {
        time: now,
        mediaId,
        x: event.clientX,
        y: event.clientY,
      };

      if (
        this.lastTap &&
        now - this.lastTap.time <= DOUBLE_TAP_DELAY_MS &&
        this.lastTap.mediaId === mediaId &&
        pointerDistance(this.lastTap, currentTap) <= DOUBLE_TAP_DISTANCE_PX
      ) {
        this.lastTap = null;
        this.toggleScale(mediaId, { x: event.clientX, y: event.clientY });
        event.preventDefault();
        return;
      }

      this.lastTap = currentTap;
    }

    stopDrag() {
      if (!this.pointerDrag) return;

      const mediaElement = this.mediaById.get(this.pointerDrag.mediaId);
      mediaElement?.classList.remove('is-panning');
      this.pointerDrag = null;
    }

    stopPointerInteractions() {
      this.stopDrag();
      this.touchPoints.clear();
      this.pinchSession = null;
      this.lastTap = null;
      this.content?.classList.remove('is-panning');
      this.mediaItems.forEach((mediaElement) => mediaElement.classList.remove('is-panning'));
    }

    startPinch(mediaId, pointA, pointB) {
      this.stopDrag();
      const current = this.getTransform(mediaId);
      this.pinchSession = {
        mediaId,
        startDistance: pointerDistance(pointA, pointB),
        startTransform: { ...current },
      };
      this.content?.classList.add('is-panning');
    }

    getTouchPointsForMedia(mediaId) {
      const points = [];
      this.touchPoints.forEach((point) => {
        if (point.mediaId === mediaId) points.push(point);
      });
      return points;
    }

    getImageForMedia(mediaElement) {
      if (!mediaElement) return null;
      if (mediaElement.classList.contains('product-media-modal__image-frame')) {
        return mediaElement.querySelector('img');
      }
      if (mediaElement.matches('img[data-media-id]')) {
        return mediaElement;
      }
      return null;
    }

    getViewportForMedia(mediaElement) {
      const width = mediaElement.clientWidth || this.content?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
      const height = mediaElement.clientHeight || this.content?.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 0);
      return { width, height };
    }

    getTransform(mediaId) {
      return this.stateByMediaId.get(mediaId) || { x: 0, y: 0, scale: MIN_SCALE };
    }

    setTransform(mediaId, transform) {
      const normalizedScale = clampScale(transform.scale);
      const normalizedTransform =
        normalizedScale <= MIN_SCALE
          ? { x: 0, y: 0, scale: MIN_SCALE }
          : { x: transform.x, y: transform.y, scale: normalizedScale };

      this.stateByMediaId.set(mediaId, normalizedTransform);
      this.applyTransform(mediaId);
    }

    setScaleAtPoint(mediaId, nextScale, clientPoint, snapshot) {
      const mediaElement = this.mediaById.get(mediaId);
      if (!mediaElement) return;

      const viewport = this.getViewportForMedia(mediaElement);
      const baseTransform = snapshot || this.getTransform(mediaId);
      const next = getNextTransformForScaleAtPoint({
        prev: baseTransform,
        nextScale,
        viewport,
        clientPoint,
      });
      const clamped = clampPan(next, viewport);

      this.setTransform(mediaId, {
        scale: next.scale,
        x: clamped.x,
        y: clamped.y,
      });
    }

    toggleScale(mediaId, point) {
      const current = this.getTransform(mediaId);
      if (current.scale > MIN_SCALE + 0.001) {
        this.resetMedia(mediaId);
        return;
      }

      this.setScaleAtPoint(mediaId, MAX_SCALE, point);
    }

    resetMedia(mediaId) {
      if (!this.stateByMediaId.has(mediaId)) return;
      this.setTransform(mediaId, { x: 0, y: 0, scale: MIN_SCALE });
    }

    resetAll() {
      this.stateByMediaId.forEach((_transform, mediaId) => {
        this.setTransform(mediaId, { x: 0, y: 0, scale: MIN_SCALE });
      });
    }

    applyTransform(mediaId) {
      const mediaElement = this.mediaById.get(mediaId);
      if (!mediaElement) return;

      const image = this.getImageForMedia(mediaElement);
      if (!image) return;

      const transform = this.getTransform(mediaId);
      image.style.transform = `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`;

      const isZoomed = transform.scale > MIN_SCALE + 0.001;
      mediaElement.classList.toggle('is-zoomed', isZoomed);
      if (!isZoomed) {
        mediaElement.classList.remove('is-panning');
      }
    }

    activateMedia(mediaId, { scrollIntoView = false } = {}) {
      if (!this.mediaById.has(mediaId)) return;

      this.activeMediaId = mediaId;
      this.mediaItems.forEach((element) => {
        element.classList.toggle('active', String(element.dataset.mediaId) === mediaId);
      });
      this.updateDots();

      if (!scrollIntoView) return;

      const target = this.mediaById.get(mediaId);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }

    updateDots() {
      if (!this.dotButtons.length) return;

      this.dotButtons.forEach((button) => {
        const isCurrent = button.dataset.mediaId === this.activeMediaId;
        if (isCurrent) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
        button.closest('.thumbnail-list__item')?.classList.toggle('is-active', isCurrent);
      });
    }

    rebuildIntersectionObserver() {
      this.intersectionObserver?.disconnect();
      if (!this.content || !this.mediaItems.length) return;

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const intersected = entries.filter((entry) => entry.isIntersecting);
          if (!intersected.length) return;

          const topEntry = intersected.reduce((best, current) =>
            current.intersectionRatio > best.intersectionRatio ? current : best
          );

          const mediaId = topEntry.target.dataset.mediaId;
          if (!mediaId) return;

          this.activeMediaId = String(mediaId);
          this.updateDots();
        },
        {
          root: this.content,
          threshold: INTERSECTION_THRESHOLDS,
        }
      );

      this.mediaItems.forEach((item) => this.intersectionObserver.observe(item));
    }

    updateActiveMediaFromViewport() {
      if (!this.content || !this.mediaItems.length) return;

      const rootRect = this.content.getBoundingClientRect();
      const centerY = rootRect.top + rootRect.height / 2;
      let bestMediaId = null;
      let bestDistance = Number.POSITIVE_INFINITY;

      this.mediaItems.forEach((item) => {
        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.top + itemRect.height / 2;
        const distance = Math.abs(itemCenter - centerY);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestMediaId = item.dataset.mediaId;
        }
      });

      if (!bestMediaId) return;
      this.activeMediaId = String(bestMediaId);
      this.updateDots();
    }
  }

  const instances = new WeakMap();

  function getController(modal) {
    if (!modal) return null;
    if (!instances.has(modal)) {
      instances.set(modal, new ProductMediaModalZoomController(modal));
    }
    return instances.get(modal);
  }

  if (typeof window !== 'undefined') {
    window.ProductMediaModalZoom = {
      getController,
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      clampScale,
      clampPan,
      getNextTransformForScaleAtPoint,
    };
  }
})();
